/**
 * ROI Check renderer (artifact_name: `check_roi`, tool `tool_check_roi`).
 * Renders the CheckROIOutput schema: return signal, cost signal, verdict table,
 * reasoning, comparison, sources, actions. Model = the parsed JSON (lossless).
 */

import React from 'react';
import { CardShell } from './shared/components';
import {
  ActionsBlock,
  Divided,
  KV,
  Pill,
  Prose,
  Section,
  SourcesBlock,
  Table,
  actionsHtml,
  humanizeEnum,
  isRecord,
  makeNarrativeEditor,
  proseHtml,
  sectionHtml,
  sourcesHtml,
  str,
} from './shared/atlas';
import { esc, htmlDoc } from './shared/html';
import type { ArtifactRenderer } from './types';

export type RoiModel = Record<string, unknown>;

const arr = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? v.filter(isRecord) : [];

// -- parse / serialize -------------------------------------------------------

export function parseRoi(json: Record<string, unknown>): RoiModel | null {
  // Identify by the ROI-specific signal blocks + verdict.
  if (!isRecord(json.return_signal) || !isRecord(json.cost_signal)) return null;
  if (!('roi_verdict' in json) && !Array.isArray(json.roi_verdict_table)) return null;
  return json;
}

const roiToJson = (m: RoiModel): Record<string, unknown> => m;

// -- card --------------------------------------------------------------------

const RoiCard: React.FC<{ model: RoiModel }> = ({ model }) => {
  const ret = isRecord(model.return_signal) ? model.return_signal : {};
  const cost = isRecord(model.cost_signal) ? model.cost_signal : {};
  const gap = isRecord(ret.gap_classification) ? ret.gap_classification : {};
  const outcomes = arr(ret.opportunity_gap);
  const factors = arr(cost.build_complexity);
  const verdictRows = arr(model.roi_verdict_table);
  const comparison = arr(model.comparison_context);

  return (
    <CardShell testId="roi-artifact-card">
      <Section
        title="ROI Verdict"
        trailing={<Pill value={str(model.roi_verdict)} />}
      >
        <Divided>
          {str(model.scope_name) && <KV label="Scope">{str(model.scope_name)}</KV>}
          {str(model.version) && <KV label="Version">{str(model.version)}</KV>}
        </Divided>
      </Section>

      <Section title="Return signal" trailing={<Pill value={str(ret.return_assessment)} />}>
        {outcomes.length > 0 && (
          <div className="mb-3">
            <Table
              head={['Outcome', 'Importance', 'Satisfaction', 'Opportunity', 'Source']}
              rows={outcomes.map((o) => [
                str(o.outcome),
                String(o.importance ?? ''),
                String(o.current_satisfaction ?? ''),
                <span className="font-semibold">{String(o.opportunity_score ?? '')}</span>,
                <span className="text-xs text-gray-400">{str(o.source)}</span>,
              ])}
            />
          </div>
        )}
        <Divided>
          {str(gap.classification) && (
            <KV label="Gap">
              <div className="flex flex-col gap-1">
                <Pill value={str(gap.classification)} className="self-start" />
                <Prose>{str(gap.reasoning)}</Prose>
              </div>
            </KV>
          )}
          {str(ret.market_context) && <KV label="Market context"><Prose>{str(ret.market_context)}</Prose></KV>}
          {str(ret.strategic_fit) && <KV label="Strategic fit"><Prose>{str(ret.strategic_fit)}</Prose></KV>}
          {str(ret.return_reasoning) && <KV label="Reasoning"><Prose>{str(ret.return_reasoning)}</Prose></KV>}
        </Divided>
      </Section>

      <Section title="Cost signal" trailing={<Pill value={str(cost.cost_assessment)} />}>
        {factors.length > 0 && (
          <div className="mb-3">
            <Table
              head={['Factor', 'Assessment', 'Source']}
              rows={factors.map((f) => [
                str(f.factor),
                str(f.assessment),
                <span className="text-xs text-gray-400">{str(f.source)}</span>,
              ])}
            />
          </div>
        )}
        <Divided>
          {str(cost.competitive_pressure) && <KV label="Competitive pressure"><Prose>{str(cost.competitive_pressure)}</Prose></KV>}
          {str(cost.stage_adjusted_reality) && <KV label="Stage reality"><Prose>{str(cost.stage_adjusted_reality)}</Prose></KV>}
          {str(cost.cost_reasoning) && <KV label="Reasoning"><Prose>{str(cost.cost_reasoning)}</Prose></KV>}
        </Divided>
      </Section>

      {verdictRows.length > 0 && (
        <Section title="Verdict table">
          <Table
            head={['Scope version', 'Return', 'Cost', 'Verdict']}
            rows={verdictRows.map((r) => [
              str(r.scope_version),
              <Pill value={str(r.return_signal)} />,
              <Pill value={str(r.cost_signal)} />,
              <Pill value={str(r.verdict)} />,
            ])}
          />
        </Section>
      )}

      <Section title="Reasoning">
        <Divided>
          {str(model.verdict_reasoning) && <KV label="Verdict"><Prose>{str(model.verdict_reasoning)}</Prose></KV>}
          {str(model.one_thing_that_would_change) && (
            <KV label="What would change it"><Prose>{str(model.one_thing_that_would_change)}</Prose></KV>
          )}
        </Divided>
      </Section>

      {comparison.length > 0 && (
        <Section title="Comparison">
          <Table
            head={['Scope', 'Return', 'Cost', 'Verdict', 'Key difference']}
            rows={comparison.map((c) => [
              str(c.scope_name),
              <Pill value={str(c.return_signal)} />,
              <Pill value={str(c.cost_signal)} />,
              <Pill value={str(c.roi_verdict)} />,
              str(c.key_difference),
            ])}
          />
        </Section>
      )}

      <SourcesBlock sources={model.sources} />
      <ActionsBlock actions={model.actions} />
    </CardShell>
  );
};

// -- editor ------------------------------------------------------------------

const RoiEditor = makeNarrativeEditor<RoiModel>(
  [
    { label: 'Verdict reasoning', rows: 3, get: (m) => str(m.verdict_reasoning), set: (m, v) => (m.verdict_reasoning = v) },
    {
      label: 'One thing that would change it',
      rows: 2,
      get: (m) => str(m.one_thing_that_would_change),
      set: (m, v) => (m.one_thing_that_would_change = v),
    },
    {
      label: 'Actions',
      rows: 4,
      hint: 'one per line',
      get: (m) => (Array.isArray(m.actions) ? (m.actions as string[]).join('\n') : ''),
      set: (m, v) => (m.actions = v.split('\n').map((s) => s.trim()).filter(Boolean)),
    },
  ],
  roiToJson
);

// -- markdown / html ---------------------------------------------------------

export function roiToMarkdown(model: RoiModel): string {
  const lines: string[] = [];
  lines.push(`# ROI Check — ${humanizeEnum(str(model.roi_verdict))}`);
  if (str(model.scope_name)) lines.push(`**Scope:** ${str(model.scope_name)} (v${str(model.version)})`);
  if (str(model.verdict_reasoning)) lines.push(`\n## Verdict reasoning\n\n${str(model.verdict_reasoning)}`);
  if (str(model.one_thing_that_would_change))
    lines.push(`\n## What would change it\n\n${str(model.one_thing_that_would_change)}`);
  const actions = Array.isArray(model.actions) ? (model.actions as string[]) : [];
  if (actions.length) lines.push(`\n## Actions\n\n${actions.map((a) => `- ${a}`).join('\n')}`);
  return lines.join('\n');
}

export function roiToHtml(model: RoiModel, title = 'ROI Check'): string {
  const ret = isRecord(model.return_signal) ? model.return_signal : {};
  const cost = isRecord(model.cost_signal) ? model.cost_signal : {};
  const body = [
    sectionHtml(
      'ROI Verdict',
      `<p><strong>${esc(humanizeEnum(str(model.roi_verdict)))}</strong> — ${esc(str(model.scope_name))} (v${esc(str(model.version))})</p>`
    ),
    sectionHtml('Return signal', proseHtml(str(ret.return_reasoning))),
    sectionHtml('Cost signal', proseHtml(str(cost.cost_reasoning))),
    sectionHtml(
      'Reasoning',
      proseHtml(str(model.verdict_reasoning)) + proseHtml(str(model.one_thing_that_would_change))
    ),
    sourcesHtml(model.sources),
    actionsHtml(model.actions),
  ].join('');
  return htmlDoc(title, body);
}

export const roiRenderer: ArtifactRenderer<RoiModel> = {
  type: 'check_roi',
  matchNames: ['check_roi', 'roi_check', 'roi'],
  parse: parseRoi,
  toJson: roiToJson,
  toMarkdown: roiToMarkdown,
  toHtml: roiToHtml,
  Card: RoiCard,
  Editor: RoiEditor,
};
